import i18n from "../../../../services/i18n"
import { useEffect, useState } from "react"
import {
  Select,
  InputNumber,
  Button,
  Table,
  Radio,
  Space,
  Popconfirm,
  message,
  Alert,
  Tag,
  Tooltip,
} from "antd"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Trash2, HelpCircle } from "react-feather"
import {
  GET_KPI_RULES,
  CREATE_KPI_RULE,
  DELETE_KPI_RULE,
} from "../../../../rest-api/queries/kpiRules"
import { GET_KPI_SETTING, UPDATE_KPI_SETTING } from "../../../../rest-api/queries/settingKPI"
import { GET_ALL_PRODUCTS } from "../../../../pages/products/queries"
import { GET_CATEGORIES } from "../../../../rest-api/queries/category"
import { GET_PRODUCT_UNITS } from "../../../../rest-api/queries/productUnit"

type RuleTarget = "product" | "category" | "unit"

/**
 * Tovar bo'yicha KPI.
 *
 * Yuqoridagi "KPI sozlamalari" to'lov turiga bog'langan. Bu yerdagi qoidalar
 * esa tovarga bog'lanadi: shu tovar sotilsa, undan qolgan toza foydaning
 * (sotuv narxi − tannarx) belgilangan foizi menejerga yoziladi.
 *
 * Qaysi usul ishlashini admin shu yerdagi tanlagich bilan belgilaydi —
 * ikkalasi bir vaqtda ishlamaydi.
 */
export default function KpiProductRules() {
  const queryClient = useQueryClient()

  const [target, setTarget] = useState<RuleTarget>("product")
  const [productId, setProductId] = useState<number | undefined>()
  const [categoryId, setCategoryId] = useState<number | undefined>()
  const [unitId, setUnitId] = useState<number | undefined>()
  const [minQty, setMinQty] = useState<number>(0)
  const [percent, setPercent] = useState<number | undefined>()
  const [mode, setMode] = useState<number>(0)

  const { data: rules, isLoading } = useQuery(["GET_KPI_RULES"], GET_KPI_RULES)
  const { data: settings } = useQuery(["GET_KPI_SETTING"], GET_KPI_SETTING)
  const { data: products } = useQuery(["GET_ALL_PRODUCTS_FOR_KPI"], () => GET_ALL_PRODUCTS({}))
  const { data: categories } = useQuery(["GET_CATEGORIES_FOR_KPI"], () => GET_CATEGORIES(""))
  const { data: units } = useQuery(["GET_UNITS_FOR_KPI"], () => GET_PRODUCT_UNITS(""))

  useEffect(() => {
    if (settings) setMode(Number(settings.mode) || 0)
  }, [settings])

  /** Serverdan kelgan xato kodini o'qiladigan matnga aylantiradi */
  function showError(err: any) {
    const code = err?.response?.data?.message || String(err)
    const translated = String(i18n.t(code))
    message.error(translated === code ? code : translated)
  }

  const createRule = useMutation(CREATE_KPI_RULE, {
    onSuccess: () => {
      message.success(String(i18n.t("kpiRuleSaved")))
      setProductId(undefined)
      setCategoryId(undefined)
      setUnitId(undefined)
      setMinQty(0)
      setPercent(undefined)
      queryClient.invalidateQueries(["GET_KPI_RULES"])
    },
    onError: showError,
  })

  const deleteRule = useMutation(DELETE_KPI_RULE, {
    onSuccess: () => {
      message.success(String(i18n.t("kpiRuleDeleted")))
      queryClient.invalidateQueries(["GET_KPI_RULES"])
    },
    onError: showError,
  })

  const saveMode = useMutation(UPDATE_KPI_SETTING, {
    onSuccess: () => {
      message.success(String(i18n.t("kpiModeSaved")))
      queryClient.invalidateQueries(["GET_KPI_SETTING"])
    },
    onError: showError,
  })

  function handleModeChange(value: number) {
    setMode(value)
    // Qolgan sozlamalar o'zgarmasligi uchun mavjud qiymatlar bilan birga yuboriladi
    saveMode.mutate({ ...(settings || {}), mode: value })
  }

  function handleAdd() {
    const chosen = target === "product" ? productId : target === "category" ? categoryId : unitId
    if (!chosen) {
      message.warning(String(i18n.t("KPI_RULE_TARGET_REQUIRED")))
      return
    }
    if (!percent || percent <= 0 || percent > 100) {
      message.warning(String(i18n.t("PERCENT_MUST_BE_1_100")))
      return
    }
    const payload: any = { minQty: minQty || 0, percent }
    if (target === "product") payload.productId = chosen
    if (target === "category") payload.categoryId = chosen
    if (target === "unit") payload.unitId = chosen
    createRule.mutate(payload)
  }

  const productOptions = (products?.data || []).map((p: any) => ({
    value: p.id,
    label: [p.article, p.nameModel].filter(Boolean).join(" — "),
  }))
  const categoryOptions = (categories?.data || []).map((c: any) => ({
    value: c.id,
    label: c.name,
  }))
  const unitOptions = (units?.data || []).map((u: any) => ({
    value: u.id,
    label: u.name,
  }))

  const currentOptions =
    target === "product" ? productOptions : target === "category" ? categoryOptions : unitOptions
  const currentValue =
    target === "product" ? productId : target === "category" ? categoryId : unitId
  const setCurrentValue =
    target === "product" ? setProductId : target === "category" ? setCategoryId : setUnitId

  const columns = [
    {
      title: i18n.t("kpiRuleTarget"),
      dataIndex: "id",
      render: (_: any, row: any) => {
        if (row?.productId) {
          return [row?.mainProduct?.article, row?.mainProduct?.nameModel]
            .filter(Boolean)
            .join(" — ")
        }
        if (row?.categoryId) {
          return (
            <Space size={6}>
              <span>{row?.productCategory?.name}</span>
              <Tag color="blue">{i18n.t("kpiRuleCategoryNote")}</Tag>
            </Space>
          )
        }
        return (
          <Space size={6}>
            <span>{row?.productUnit?.name}</span>
            <Tag color="purple">{i18n.t("kpiRuleUnitNote")}</Tag>
          </Space>
        )
      },
    },
    {
      title: i18n.t("kpiRuleMinQty"),
      dataIndex: "minQty",
      width: 140,
      align: "center" as const,
      render: (v: number) => (v > 0 ? v : "—"),
    },
    {
      title: i18n.t("kpiRulePercent"),
      dataIndex: "percent",
      width: 120,
      align: "center" as const,
      render: (v: number) => `${v} %`,
    },
    {
      title: "",
      dataIndex: "actions",
      width: 60,
      align: "center" as const,
      render: (_: any, row: any) => (
        <Popconfirm
          title={String(i18n.t("kpiRuleDeleteConfirm"))}
          okText={String(i18n.t("yes"))}
          cancelText={String(i18n.t("cancel"))}
          onConfirm={() => deleteRule.mutate(row.id)}
        >
          <Button type="text" danger size="small" icon={<Trash2 size={14} />} />
        </Popconfirm>
      ),
    },
  ]

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>{i18n.t("kpiModeTitle")}</div>
        <Radio.Group
          value={mode}
          onChange={(e) => handleModeChange(e.target.value)}
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button value={0}>{i18n.t("kpiModeByPayment")}</Radio.Button>
          <Radio.Button value={1}>{i18n.t("kpiModeByProduct")}</Radio.Button>
        </Radio.Group>
        <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 6 }}>
          {i18n.t("kpiModeHint")}
        </div>
      </div>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12, fontSize: 12, borderRadius: 6 }}
        message={String(i18n.t("kpiRulesHint"))}
      />

      <Space wrap style={{ marginBottom: 12, width: "100%" }} align="center">
        <Radio.Group
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button value="product">{i18n.t("kpiRuleByProduct")}</Radio.Button>
          <Radio.Button value="category">{i18n.t("kpiRuleByCategory")}</Radio.Button>
          <Radio.Button value="unit">{i18n.t("kpiRuleByUnit")}</Radio.Button>
        </Radio.Group>

        <Select
          showSearch
          allowClear
          style={{ minWidth: 250 }}
          placeholder={String(i18n.t("kpiRuleChooseTarget"))}
          value={currentValue}
          onChange={setCurrentValue}
          options={currentOptions}
          filterOption={(input, option) =>
            String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />

        <Space size={4} align="center">
          <InputNumber
            min={0}
            value={minQty}
            onChange={(v) => setMinQty((v as number) ?? 0)}
            placeholder={String(i18n.t("kpiRuleMinQty"))}
            style={{ width: 160 }}
          />
          <Tooltip title={String(i18n.t("kpiRuleMinQtyHint"))}>
            <HelpCircle size={14} color="#94A3B8" />
          </Tooltip>
        </Space>

        <InputNumber
          min={1}
          max={100}
          value={percent}
          onChange={(v) => setPercent(v as number)}
          placeholder={String(i18n.t("kpiRulePercent"))}
          style={{ width: 140 }}
        />

        <Button type="primary" loading={createRule.isLoading} onClick={handleAdd}>
          {i18n.t("kpiRuleAdd")}
        </Button>
      </Space>

      <Table
        size="small"
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={rules || []}
        pagination={false}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: String(i18n.t("kpiRuleEmpty")) }}
      />
    </div>
  )
}
